/**
 * product-tabs.js
 * Web Component for the "Product tabs" section.
 * Custom element name: hc-tab-group  (prefixed to avoid third-party conflicts)
 */

class HcTabGroup extends HTMLElement {
  connectedCallback() {
    // Defer so that all child blocks are guaranteed to be in the DOM
    // (connectedCallback can fire before children are parsed)
    setTimeout(() => this._init(), 0);
  }

  _init() {
    this._nav = this.querySelector('.product-tabs__nav');
    this._panelContainer = this.querySelector('.product-tabs__panels');

    if (!this._nav || !this._panelContainer) return;

    this._panels = Array.from(
      this._panelContainer.querySelectorAll('.product-tabs__panel')
    );

    if (!this._panels.length) return;

    this._tabs = [];
    this._activeIndex = -1;
    this._checking = false;
    this._observerTimer = null;
    this._inEditor = Boolean(window.Shopify?.designMode);

    this._buildTabs();

    if (this._inEditor) {
      this._activateTab(0);
    } else {
      this._performEmptyCheck();
      this._activateFirst();
      this._setupObserver();
      setTimeout(() => this._performEmptyCheck(true), 1500);
      setTimeout(() => this._performEmptyCheck(true), 4000);
    }
  }

  _buildTabs() {
    const groupId = this.id || `hc-tabs-${Math.random().toString(36).slice(2, 7)}`;

    this._panels.forEach((panel, i) => {
      const label = panel.dataset.tabLabel || `Tab ${i + 1}`;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-tabs__tab';
      btn.textContent = label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('id', `${groupId}-tab-${i}`);
      btn.setAttribute('aria-controls', `${groupId}-panel-${i}`);
      btn.dataset.index = String(i);

      panel.setAttribute('id', `${groupId}-panel-${i}`);
      panel.setAttribute('aria-labelledby', `${groupId}-tab-${i}`);

      btn.addEventListener('click', () => this._activateTab(i));

      this._nav.appendChild(btn);
      this._tabs.push(btn);

      panel.hidden = true;
    });
  }

  _activateFirst() {
    const first = this._tabs.findIndex(t => !t.hidden);
    if (first >= 0) this._activateTab(first);
  }

  _activateTab(index) {
    this._activeIndex = index;
    this._tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    this._panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }

  _isPanelEmpty(panel) {
    if (panel.textContent.trim().length > 0) return false;
    if (panel.querySelector('img, iframe, video, canvas, svg')) return false;
    return true;
  }

  _performEmptyCheck(afterLoad = false) {
    if (this._checking) return;
    this._checking = true;

    this._panels.forEach((panel, i) => {
      if (panel.dataset.hideIfEmpty !== 'true') return;
      const tab = this._tabs[i];
      if (!tab) return;

      const wasTabHidden = tab.hidden;
      const isEmpty = this._isPanelEmpty(panel);
      tab.hidden = isEmpty;

      if (afterLoad) {
        if (wasTabHidden && !isEmpty) {
          const activeTab = this._tabs[this._activeIndex];
          if (!activeTab || activeTab.hidden) this._activateTab(i);
        }
        if (!wasTabHidden && isEmpty && this._activeIndex === i) {
          this._activateFirst();
        }
      }
    });

    this._checking = false;
  }

  _setupObserver() {
    const observer = new MutationObserver(() => {
      clearTimeout(this._observerTimer);
      this._observerTimer = setTimeout(() => {
        if (!this._checking) this._performEmptyCheck(true);
      }, 300);
    });

    this._panels.forEach(panel => {
      if (panel.dataset.hideIfEmpty !== 'true') return;
      observer.observe(panel, { childList: true, subtree: true, characterData: true });
    });
  }
}

if (!customElements.get('hc-tab-group')) {
  customElements.define('hc-tab-group', HcTabGroup);
}
